/**
 * Mode Engine - Question Selection Orchestration
 *
 * Central orchestrator for mode-specific question selection.
 * Delegates to mode-specific selectors based on mode configuration.
 *
 * NOTE: "Simulation" mode removed — merged with Assessment as "Toets" in Learning Lab.
 * NOTE: "Free" mode handled directly in usePracticeSession (does NOT pass through
 *        this engine) — see types.ts header for rationale.
 */

import { type Question } from '../content'
import { AppState } from '../app/types'
import { GroupId } from '../config/systemConfig'
import {
  ModeId,
  FluencyConfig,
  SprintConfig,
  ChallengeConfig,
  ExperimentsConfig,
  ModeSessionConfig,
} from './types'
import {
  getFluencyQuestions,
  getSprintQuestions,
  getChallengeQuestions,
  getExperimentsQuestions,
} from './modeSelectors'
import { getAllQuestions } from '../content'

/**
 * Result of creating a mode session — contains the selected questions.
 */
export interface ModeSessionResult {
  sessionId: string
  modeId: ModeId
  questions: Question[]
  config: ModeSessionConfig
}

/**
 * Modes that this engine actually dispatches. 'free' is intentionally excluded
 * (handled upstream in usePracticeSession).
 */
type DispatchableModeId = Exclude<ModeId, 'free'>

/**
 * Create a new mode session with selected questions.
 *
 * Throws if no questions can be selected for the requested mode.
 */
export function createModeSession(
  modeId: ModeId,
  config: ModeSessionConfig,
  state: AppState,
  selectedGroup: GroupId,
): ModeSessionResult {
  const sessionId = `${modeId}_${Date.now()}`
  const allQuestions = getAllQuestions()

  const questions = selectQuestionsForMode(
    modeId,
    config,
    allQuestions,
    state,
    selectedGroup,
  )
  if (questions.length === 0) {
    throw new Error(`No questions available for mode ${modeId}`)
  }
  return { sessionId, modeId, questions, config }
}

/**
 * Select questions for a specific mode.
 *
 * Each branch narrows the config to the type its selector expects.
 */
function selectQuestionsForMode(
  modeId: ModeId,
  config: ModeSessionConfig,
  allQuestions: Question[],
  state: AppState,
  selectedGroup: GroupId,
): Question[] {
  const dispatchable = modeId as DispatchableModeId
  switch (dispatchable) {
    case 'fluency':
      return getFluencyQuestions(
        config as FluencyConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'sprint':
      return getSprintQuestions(
        config as SprintConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'challenge':
      return getChallengeQuestions(
        config as ChallengeConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    case 'experiments':
      return getExperimentsQuestions(
        config as ExperimentsConfig,
        allQuestions,
        state,
        selectedGroup,
      )
    default: {
      // Exhaustive check: if a new dispatchable ModeId is added, TS will fail here.
      const _exhaustive: never = dispatchable
      throw new Error(`Unsupported mode: ${modeId} (${String(_exhaustive)})`)
    }
  }
}
